import { PropsWithChildren, ReactElement, ReactNode } from "react";
import { Toaster as Sonner, toast as sonnerToast, useSonner } from "sonner";
import { Error, Insight, Success, Warning, Close, Info } from "@/assets";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { AnimateSlideFromBottom } from "./animations";
import { toastVariants } from "./toast";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
    const { toasts } = useSonner();

    return (
        <div className="relative">
            <Sonner
                className="toaster group"
                toastOptions={{
                    classNames: {
                        toast: "group toast",
                    },
                }}
                offset={toasts.length > 1 ? { bottom: 50 } : undefined}
                {...props}
            />
            <AnimateSlideFromBottom
                show={toasts.length > 1}
                className="fixed right-6 bottom-4 z-100"
            >
                <Button
                    appearance="subtle"
                    onClick={() => sonnerToast.dismiss()}
                    className="bg-elevation-surface-default hover:bg-elevation-surface-hover active:bg-elevation-surface-press shadow-overlay"
                >
                    Clear all
                </Button>
            </AnimateSlideFromBottom>
        </div>
    );
};

export type ToastProps = {
    id: string | number;
    title?: string;
    description?: ReactNode;
    action?: ReactElement;
    appearance?:
        | "default"
        | "information"
        | "announcement"
        | "success"
        | "warning"
        | "danger";
    isBold?: boolean;
    className?: string;
};

export const toast = (toast: Omit<ToastProps, "id"> & { id?: string }) => {
    return sonnerToast.custom(
        (id: string | number) => <Toast id={id} {...toast} />,
        toast.id ? { id: toast.id } : undefined,
    );
};

const ToastIcon = ({ children }: PropsWithChildren) => (
    <div className="h-6 w-6 shrink-0 self-start">{children}</div>
);

const Toast = (props: ToastProps) => {
    const {
        title,
        description,
        action,
        appearance = "default",
        isBold = false,
        id,
        className,
    } = props;

    return (
        <div className={cn(toastVariants({ appearance, isBold }), className)}>
            {appearance !== "default" && (
                <ToastIcon>
                    {appearance === "information" && <Info />}
                    {appearance === "announcement" && <Insight />}
                    {appearance === "success" && <Success />}
                    {appearance === "warning" && <Warning />}
                    {appearance === "danger" && <Error />}
                </ToastIcon>
            )}
            <div className="gap-narrow flex flex-col items-start">
                {title && (
                    // Same as HeadingElementTitle, but need inverse text to overwrite
                    <div className="font-body text-large leading-default font-bolder pr-6">
                        {title}
                    </div>
                )}
                {description && (
                    // Same as BodyDefault, but need inverse styles to overwrite
                    <div className="text-default font-default leading-default">
                        {description}
                    </div>
                )}
                {action}
            </div>
            <button
                onClick={() => sonnerToast.dismiss(id)}
                title="close toast"
                className="absolute top-2 right-2 cursor-pointer rounded-md p-1 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:ring-2 focus:outline-hidden"
            >
                <Close />
            </button>
        </div>
    );
};

export { Toaster };
